import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';

export default function AIActionItemSuggestions({ 
  open, 
  onClose, 
  onSelect, 
  goal,
  existingItems = [] 
}) {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const addButtonRef = useRef(null);

  const handleGenerateSuggestion = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GOOGLE_AI_API_KEY);
      const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

      // Get date one month from now
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
      const formattedDate = oneMonthFromNow.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });

      const prompt = `
        As an AI career development assistant, suggest an action item for the following development goal:
        
        Goal: ${goal.goal}

        Context:
        - Current action items: ${existingItems.map(item => item.text).join(', ')}
        
        Generate a specific, actionable item that:
        1. Helps achieve the development goal
        2. Is concrete and measurable
        3. Can be completed within 1 month
        4. Builds on existing action items without duplicating them
        
        Format the response in plain text exactly like this:
        Action Item: [The specific action to take]
        Due Date: ${formattedDate}
        Reasoning: [Brief explanation of how this action item contributes to the goal]
      `;

      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();

      // Parse the response
      const lines = text.split('\n').filter(line => line.trim());
      const parsedSuggestion = {
        text: lines[0].replace('Action Item:', '').trim(),
        dueDate: oneMonthFromNow, // Use the date we calculated
        reasoning: lines[2].replace('Reasoning:', '').trim()
      };

      setSuggestion(parsedSuggestion);
    } catch (error) {
      console.error('AI Suggestion Error:', error);
      setError('Failed to generate suggestion. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuggestion(null);
    setError(null);
    onClose();
  };

  const handleSelect = () => {
    if (suggestion) {
      onSelect({
        text: suggestion.text,
        dueDate: suggestion.dueDate,
        status: 'On track'
      });
      handleClose();
    }
  };

  // Generate suggestion when dialog opens
  React.useEffect(() => {
    if (open) {
      handleGenerateSuggestion();
    }
  }, [open]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      keepMounted={false}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 2
      }}>
        <Typography variant="h6">AI-Suggested Action Item</Typography>
        <Button 
          onClick={handleGenerateSuggestion}
          startIcon={<AutoAwesomeIcon />}
          disabled={loading}
          sx={{
            backgroundColor: 'grey.100',
            color: 'text.primary',
            textTransform: 'none',
            border: '1px solid transparent',
            '&:hover': {
              backgroundColor: 'common.white',
              border: '1px solid',
              borderColor: 'primary.main',
            }
          }}
        >
          Regenerate
        </Button>
        
        </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : suggestion ? (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="h6">
                {suggestion.text}
              </Typography>
              
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Suggested Due Date
              </Typography>
              <Typography paragraph>
                {suggestion.dueDate.toLocaleDateString()}
              </Typography>

              <Typography variant="subtitle2" color="primary" gutterBottom>
                Reasoning
              </Typography>
              <Typography paragraph>
                {suggestion.reasoning}
              </Typography>
            </Box>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          ref={addButtonRef}
          onClick={handleSelect}
          variant="contained"
          disabled={!suggestion || loading}
        >
          Add Action Item
        </Button>
      </DialogActions>
    </Dialog>
  );
} 